import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface RatingComparisonChartProps {
  contractors: PerformanceSummary[];
}

export function RatingComparisonChart({ contractors }: RatingComparisonChartProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const availableContractors = contractors.filter(c => c.overall_rating);
  const selectedContractors = contractors.filter(c => selectedIds.includes(c.id));

  const handleSelect = (id: string) => {
    if (selectedIds.length < 5 && !selectedIds.includes(id)) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleRemove = (id: string) => {
    setSelectedIds(selectedIds.filter(cid => cid !== id));
  };

  const chartData = [
    { category: 'Safety', ...selectedContractors.reduce((acc, c, i) => ({ ...acc, [`contractor${i}`]: c.safety_rating || 0 }), {}) },
    { category: 'Quality', ...selectedContractors.reduce((acc, c, i) => ({ ...acc, [`contractor${i}`]: c.quality_rating || 0 }), {}) },
    { category: 'Reliability', ...selectedContractors.reduce((acc, c, i) => ({ ...acc, [`contractor${i}`]: c.reliability_rating || 0 }), {}) },
    { category: 'Overall', ...selectedContractors.reduce((acc, c, i) => ({ ...acc, [`contractor${i}`]: c.overall_rating || 0 }), {}) },
  ];

  const colors = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contractor Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Select onValueChange={handleSelect} disabled={selectedIds.length >= 5}>
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Select contractor to compare..." />
              </SelectTrigger>
              <SelectContent>
                {availableContractors
                  .filter(c => !selectedIds.includes(c.id))
                  .map((contractor) => (
                    <SelectItem key={contractor.id} value={contractor.id}>
                      {contractor.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {selectedIds.length >= 5 && (
              <span className="text-xs text-muted-foreground">Max 5 contractors</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {selectedContractors.map((contractor, index) => (
              <Badge key={contractor.id} variant="secondary" className="gap-1">
                <span style={{ color: colors[index] }}>●</span>
                {contractor.name}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={() => handleRemove(contractor.id)}
                />
              </Badge>
            ))}
          </div>

          {selectedContractors.length > 0 ? (
            <ChartContainer config={{}} className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={chartData}>
                  <PolarGrid className="stroke-muted" />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 5]}
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  {selectedContractors.map((contractor, index) => (
                    <Radar
                      key={contractor.id}
                      name={contractor.name}
                      dataKey={`contractor${index}`}
                      stroke={colors[index]}
                      fill={colors[index]}
                      fillOpacity={0.2}
                    />
                  ))}
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-muted-foreground">
              Select contractors to compare their ratings
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
